import React from 'react'
import { MatchedJobForm } from 'types/interfaces'
import MatchedJobFormError from 'errors/MatchedJobFormError'
import { Alert, Box, Button, Typography } from '@mui/material'
import FormField from 'components/FormField'
import MultiSelect from 'components/MultiSelect'
import update from 'immutability-helper'
import { Role } from 'types/types'
import { concat } from 'lodash'
import ErrorList from 'components/ErrorList'
import { PostPrematchedJobStatus } from './index'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

type PrematchedJobFormProps = {
    role: Role
    form: MatchedJobForm
    error?: MatchedJobFormError
    status: PostPrematchedJobStatus
    updateForm: (name: string, value: any) => void
    setForm: (form: MatchedJobForm) => void
    addForm: (form?: MatchedJobForm) => void
    removeForm: () => void
    saving: boolean
}

const PrematchedJobForm: React.FC<PrematchedJobFormProps> = ({ role, form, error, status, updateForm, setForm, addForm, removeForm, saving }) => {
    const updateSpecialistRequirements = React.useCallback((value: string[]) => {
        setForm(update(form, {
            sedation_required: { $set: value.includes('sedation') },
            radiography_required: { $set: value.includes('radiography') },
            implant_required: { $set: value.includes('implant') },
            orthodontics_required: { $set: value.includes('orthodontics') },
        }))
    }, [setForm, form])

    const specialistRequirements = React.useMemo(() => {
        const requirements = []
        if (form.sedation_required) requirements.push('sedation')
        if (form.radiography_required) requirements.push('radiography')
        if (form.implant_required) requirements.push('implant')
        if (form.orthodontics_required) requirements.push('orthodontics')
        return requirements
    }, [form.sedation_required, form.radiography_required, form.implant_required, form.orthodontics_required])

    const specialistRequirementsErrors = React.useMemo(() => {
        return concat(
            error?.sedation_required ?? [],
            error?.radiography_required ?? [],
            error?.implant_required ?? [],
            error?.orthodontics_required ?? [],
        )
    }, [error])

    const duplicateForm = React.useCallback(() => {
        addForm(form)
    }, [addForm, form])
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <FormField
                    name='date'
                    label='Job Date'
                    type='date'
                    sx={{ width: 200 }}
                    InputLabelProps={{ shrink: true }}
                    value={form.date ?? ''}
                    onChange={(e) => updateForm('date', e.target.value)}
                    errors={error?.date}
                />
                <FormField
                    name='start_time'
                    label='Start Time'
                    type='time'
                    sx={{ width: 200 }}
                    InputLabelProps={{ shrink: true }}
                    value={form.start_time ?? ''}
                    onChange={(e) => updateForm('start_time', e.target.value)}
                    errors={error?.start_time}
                />
                <FormField
                    name='end_time'
                    label='End Time'
                    type='time'
                    sx={{ width: 200 }}
                    InputLabelProps={{ shrink: true }}
                    value={form.end_time ?? ''}
                    onChange={(e) => updateForm('end_time', e.target.value)}
                    errors={error?.end_time}
                />
                <FormField
                    name='lunch_break'
                    label='Lunch Break (minutes)'
                    type='number'
                    sx={{ width: 200 }}
                    value={form.lunch_break ?? ''}
                    onChange={(e) => updateForm('lunch_break', e.target.value)}
                    errors={error?.lunch_break}
                />
                {/* TODO: add specialist requirements back in when it's ready to go live */}
                {/* {role === 'dental nurse' ? (
                    <MultiSelect
                        name='specialist_requirements'
                        label='Specialist Requirements'
                        width={250}
                        options={['sedation', 'radiography', 'implant', 'orthodontics']}
                        onChange={(e) => updateSpecialistRequirements(e.target.value as string[])}
                        value={specialistRequirements}
                        errors={specialistRequirementsErrors}
                    />  
                ) : null} */}
                <Box sx={{ flex: 1 }} />
                {status === 'pending' ? (
                    <React.Fragment>
                        <Button variant='outlined' color='error' onClick={removeForm} disabled={saving}>
                            Remove
                        </Button>
                        <Button variant='outlined' color='secondary' onClick={duplicateForm} disabled={saving}>
                            Duplicate
                        </Button>
                    </React.Fragment>
                ) : status === 'success' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                        <CheckCircleIcon color='success' />
                        <Typography variant='body1' sx={{ color: 'success.main' }}>Job posted successfully</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                        <ErrorIcon color='error' />
                        <Typography variant='body1' sx={{ color: 'error.main' }}>Job posting failed</Typography>
                    </Box>
                )}
            </Box>
            {error?.schema ? (
                <Alert severity='error' variant='filled'><ErrorList errors={error.schema} /></Alert>
            ) : null}
        </Box>
    )
}

export default PrematchedJobForm